import React from 'react';
import { motion } from 'framer-motion';
import useT from '../../../i18n';

/**
 * Editorial hero — Fase 2 leader. Typography-only (no image in text column).
 * Subtile SVG fjord-line at the bottom acts as an Aesop-esque taktil detail.
 *
 * Motion: eyebrow → headline → sub-copy → CTAs staggered fade+translate at
 * mount only (never re-triggers).
 */
export default function Hero({ onPrimary, onSecondary }) {
  const t = useT();
  const hero = t.landingV2.hero;

  const item = {
    hidden: { opacity: 0, y: 12 },
    show: (i) => ({
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1], delay: 0.05 + i * 0.1 },
    }),
  };

  return (
    <section
      className="relative min-h-[92vh] flex items-center overflow-hidden bg-paper"
      data-testid="landing-hero"
    >
      {/* Atmospheric Norwegian fjord backdrop — paper gradients keep ink text readable */}
      <div className="absolute inset-0" aria-hidden="true">
        <img
          src="/assets/hero/livsvei-hero.jpg"
          alt=""
          className="w-full h-full object-cover object-[68%_center] md:object-center"
          loading="eager"
          fetchPriority="high"
          data-testid="hero-backdrop-image"
        />
        {/* Mobile: vertical veil — readable text on top, fjord visible below */}
        <div className="absolute inset-0 md:hidden bg-gradient-to-b from-paper via-paper/85 to-paper/25" />
        {/* Desktop: horizontal veil — solid paper left, fjord emerges right */}
        <div className="absolute inset-0 hidden md:block bg-gradient-to-r from-paper via-paper/85 to-paper/25" />
        <div className="absolute inset-0 hidden md:block bg-gradient-to-t from-paper via-paper/30 to-paper/40" />
      </div>
      <div className="relative z-10 max-w-editorial mx-auto w-full px-6 md:px-10 py-24 md:py-32">
        <motion.p
          initial="hidden"
          animate="show"
          variants={item}
          custom={0}
          className="font-sans text-xs uppercase tracking-widest text-ink/50"
          data-testid="hero-eyebrow"
        >
          {hero.eyebrow}
        </motion.p>

        <motion.h1
          initial="hidden"
          animate="show"
          variants={item}
          custom={1}
          className="mt-6 font-display font-light text-display-xl text-ink whitespace-pre-line"
          data-testid="hero-headline"
        >
          {hero.headline}
        </motion.h1>

        <motion.p
          initial="hidden"
          animate="show"
          variants={item}
          custom={2}
          className="mt-8 font-sans text-lg md:text-xl text-ink/70 max-w-prose-wide leading-relaxed"
          data-testid="hero-subcopy"
        >
          {hero.subCopy}
        </motion.p>

        <motion.div
          initial="hidden"
          animate="show"
          variants={item}
          custom={3}
          className="mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6"
        >
          <button
            onClick={onPrimary}
            className="inline-flex items-center gap-2 bg-ink text-paper px-8 py-4 rounded-full font-sans text-sm font-medium hover:bg-ink/90 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-fjord/40 focus-visible:ring-offset-2 focus-visible:ring-offset-paper"
            data-testid="hero-cta-primary"
          >
            {hero.ctaPrimary}
          </button>
          <button
            onClick={onSecondary}
            className="font-sans text-sm text-ink/70 hover:text-ink underline underline-offset-8 decoration-ink/20 hover:decoration-ink/40 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-fjord/40 focus-visible:ring-offset-4 focus-visible:ring-offset-paper rounded-sm"
            data-testid="hero-cta-secondary"
          >
            {hero.ctaSecondary} <span aria-hidden>→</span>
          </button>
        </motion.div>
      </div>

      {/* Subtile fjord-line SVG at bottom — 1px stroke, taktil editorial detail */}
      <svg
        className="absolute inset-x-0 bottom-0 w-full pointer-events-none select-none z-10"
        viewBox="0 0 1440 120"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <path
          d="M0,85 Q140,72 280,80 T560,72 T840,84 T1120,76 T1440,80 L1440,120 L0,120 Z"
          fill="var(--fjord)"
          fillOpacity="0.03"
        />
        <path
          d="M0,85 Q140,72 280,80 T560,72 T840,84 T1120,76 T1440,80"
          fill="none"
          stroke="var(--fjord)"
          strokeOpacity="0.25"
          strokeWidth="1"
        />
        <path
          d="M0,102 Q180,96 360,98 T720,94 T1080,100 T1440,96"
          fill="none"
          stroke="var(--fjord)"
          strokeOpacity="0.12"
          strokeWidth="1"
        />
      </svg>
    </section>
  );
}
